import { z } from 'zod';
import { paginationQuerySchema } from '../common/envelope.js';
/** At least one of these is required — the timeline is always scoped to a
 * record (docs/architecture screen "Activities feed" / the per-record
 * "Timeline" tab on Lead/Contact/Company/Deal detail). */
export const listActivitiesQuerySchema = paginationQuerySchema
    .extend({
    leadId: z.string().uuid().optional(),
    contactId: z.string().uuid().optional(),
    companyId: z.string().uuid().optional(),
    opportunityId: z.string().uuid().optional(),
})
    .refine((v) => Boolean(v.leadId || v.contactId || v.companyId || v.opportunityId), {
    message: 'At least one of leadId, contactId, companyId or opportunityId is required',
});
/** The standalone Activities feed (docs/architecture/11-ui-sitemap.md) —
 * unlike the per-record timeline above, this is scoped by the caller's own
 * `own`/`team`/`department`/`all` visibility (docs/architecture/06-rbac.md
 * § 3), not by one record's. */
export const activityKindSchema = z.enum(['call', 'email', 'meeting', 'note', 'system', 'assigned', 'stage_changed', 'task_completed']);
export const listGlobalActivitiesQuerySchema = paginationQuerySchema.extend({
    kind: activityKindSchema.optional(),
    occurredFrom: z.string().datetime().optional(),
    occurredTo: z.string().datetime().optional(),
    actorId: z.string().uuid().optional(),
});
export const activityRelatedToSchema = z.object({
    type: z.enum(['lead', 'contact', 'company', 'opportunity']),
    id: z.string().uuid(),
    label: z.string(),
});
export const activityResponseSchema = z.object({
    id: z.string().uuid(),
    kind: z.string(),
    subject: z.string().nullable(),
    /** Always plain text — see `Activity.bodyText` in schema.prisma for why
     * the field isn't called bodyHtml. */
    bodyText: z.string().nullable(),
    outcome: z.string().nullable(),
    occurredAt: z.string().datetime(),
    actor: z.object({ id: z.string().uuid(), displayName: z.string() }).nullable(),
    metadata: z.record(z.string(), z.unknown()),
    relatedTo: activityRelatedToSchema.nullable().optional(),
});
export const createNoteActivitySchema = z.object({
    bodyText: z.string().trim().min(1).max(10_000),
    leadId: z.string().uuid().optional(),
    contactId: z.string().uuid().optional(),
    companyId: z.string().uuid().optional(),
    opportunityId: z.string().uuid().optional(),
});
/** The record-detail "log activity" composer (design/orion-crm-design/
 * rendered-html/LeadDetail.html) — a superset of `createNoteActivitySchema`
 * covering the 3 kinds that are actually loggable this way today
 * (`activityKindSchema` above already anticipated 'call'/'email'/'meeting'
 * as real kinds; nothing wrote them until this). `outcome`/`durationSeconds`
 * only make sense for calls, so both stay optional for the others. */
export const loggableActivityKindSchema = z.enum(['note', 'call', 'email', 'meeting']);
export const logActivitySchema = z.object({
    kind: loggableActivityKindSchema,
    bodyText: z.string().trim().min(1).max(10_000),
    outcome: z.string().trim().max(100).optional(),
    durationSeconds: z.number().int().min(0).max(86_400).optional(),
    leadId: z.string().uuid().optional(),
    contactId: z.string().uuid().optional(),
    companyId: z.string().uuid().optional(),
    opportunityId: z.string().uuid().optional(),
});
/** docs screen "Activities feed" § right rail — real aggregates over the
 * caller's visible activity for the given window (fetch-then-reduce-in-JS,
 * this codebase's convention), not a hardcoded illustration. */
export const activityWeeklySummarySchema = z.object({
    totalCount: z.number().int(),
    byKind: z.array(z.object({ kind: z.string(), count: z.number().int() })),
    byRep: z.array(z.object({
        membershipId: z.string().uuid(),
        displayName: z.string(),
        total: z.number().int(),
        byKind: z.array(z.object({ kind: z.string(), count: z.number().int() })),
    })),
});
export const staleLeadSchema = z.object({ id: z.string().uuid(), name: z.string() });
export const staleLeadsResponseSchema = z.object({ count: z.number().int(), sample: z.array(staleLeadSchema) });
//# sourceMappingURL=schemas.js.map